import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { 
  AlertTriangle,
  RefreshCw,
  Download,
  TrendingUp,
  TrendingDown,
  Minus,
  Users,
  Wrench,
  ChevronDown,
  ChevronUp,
  Info,
  Target
} from 'lucide-react';
import { InfoTooltip, SectionHeader, DefinitionsPanel, EmptyState } from '../components/ui/info-components';
import { secureExportDownload } from '../utils/secureExport';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Cache simple
const cache = new Map();
const CACHE_TTL = 5 * 60 * 1000;

export default function FCRPorIncidenciaView({ token, selectedPartner = 'global' }) {
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('ytd');
  const [data, setData] = useState(null);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [exporting, setExporting] = useState(false);
  
  // Estado para toggle local: permitir ver "todas" incluso si viene un partner específico
  const [viewAllInstances, setViewAllInstances] = useState(selectedPartner === 'global');
  
  // Instancia efectiva: si viewAllInstances está activo, usar 'global' (todas)
  const effectivePartner = viewAllInstances ? 'global' : selectedPartner;
  
  const fetchingRef = useRef(false);

  const getCacheKey = useCallback(() => {
    const instancia = effectivePartner !== 'global' ? effectivePartner : '';
    return `fcr-incidencia-${period}-${instancia}`;
  }, [period, effectivePartner]);

  const fetchData = useCallback(async (forceRefresh = false) => {
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    
    const cacheKey = getCacheKey();
    const cached = cache.get(cacheKey);
    
    if (!forceRefresh && cached && Date.now() - cached.timestamp < CACHE_TTL) {
      setData(cached.data);
      setLoading(false);
      fetchingRef.current = false;
      return;
    }
    
    setLoading(true);
    const instancia = effectivePartner !== 'global' ? effectivePartner : '';
    const params = `period=${period}${instancia ? `&instancia=${instancia}` : ''}`;
    
    try {
      const res = await fetch(`${API_URL}/api/atencion/derivaciones/fcr-por-incidencia?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const responseData = await res.json();
        setData(responseData);
        cache.set(cacheKey, { data: responseData, timestamp: Date.now() });
      }
    } catch (error) {
      console.error('Error fetching FCR por incidencia:', error);
    }
    setLoading(false);
    fetchingRef.current = false;
  }, [token, period, selectedPartner, getCacheKey]);

  const handleExportCSV = async () => {
    setExporting(true);
    const instancia = effectivePartner !== 'global' ? effectivePartner : '';
    const params = `period=${period}${instancia ? `&instancia=${instancia}` : ''}`;
    
    try {
      const url = `${API_URL}/api/atencion/derivaciones/fcr-por-incidencia/export?${params}`;
      await secureExportDownload({ url, filename: `fcr_por_incidencia_${period}_${effectivePartner}.xlsx` });
    } catch (error) {
      if (error.message !== 'cancelado') {
        console.error('Error exporting CSV:', error);
      }
    }
    setExporting(false);
  };

  const toggleRow = (incidencia) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(incidencia)) {
      newExpanded.delete(incidencia);
    } else {
      newExpanded.add(incidencia);
    }
    setExpandedRows(newExpanded);
  };

  useEffect(() => {
    if (token) {
      fetchData();
    }
  }, [token, period, effectivePartner]);

  // Loading skeleton
  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="animate-pulse h-6 bg-gray-200 rounded w-64"></div>
          <div className="flex gap-2">
            <div className="animate-pulse h-9 bg-gray-200 rounded w-32"></div>
            <div className="animate-pulse h-9 bg-gray-200 rounded w-28"></div>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="animate-pulse bg-gray-100 rounded-xl h-24"></div>
          ))}
        </div>
        <div className="animate-pulse bg-gray-100 rounded-xl h-96"></div>
      </div>
    );
  }

  const resumen = data?.resumen || {};
  const incidencias = data?.incidencias || [];
  const definiciones = data?.definiciones || {};

  // Colores para tipo de brecha
  const getTipoBrechaStyle = (tipo) => {
    switch (tipo) {
      case 'PROCESO':
        return { bg: 'bg-orange-100', text: 'text-orange-700', border: 'border-orange-200', icon: Wrench };
      case 'PERSONA':
        return { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-200', icon: Users };
      case 'MIXTO':
        return { bg: 'bg-purple-100', text: 'text-purple-700', border: 'border-purple-200', icon: AlertTriangle };
      default:
        return { bg: 'bg-gray-100', text: 'text-gray-600', border: 'border-gray-200', icon: Info };
    }
  };

  // Icono de tendencia
  const getTendenciaIcon = (tendencia) => {
    switch (tendencia) {
      case '↑': return <TrendingUp className="w-4 h-4 text-green-500" />;
      case '↓': return <TrendingDown className="w-4 h-4 text-red-500" />;
      case '→': return <Minus className="w-4 h-4 text-gray-400" />;
      default: return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <SectionHeader 
            title="FCR por Incidencia"
            description="Identifica si las fallas en FCR son por PROCESO (transversal) o por PERSONA (capacitación)."
            icon={Target}
          />
          {/* Toggle Todas/Instancia + Badge */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => setViewAllInstances(true)}
              className={`px-3 py-1 text-sm rounded-l-lg border transition-colors ${
                viewAllInstances 
                  ? 'bg-purple-100 text-purple-700 border-purple-300 font-medium' 
                  : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
              }`}
            >
              🌐 Todas
            </button>
            <button
              onClick={() => setViewAllInstances(false)}
              disabled={selectedPartner === 'global'}
              className={`px-3 py-1 text-sm rounded-r-lg border-t border-r border-b transition-colors ${
                !viewAllInstances && selectedPartner !== 'global'
                  ? 'bg-blue-100 text-blue-700 border-blue-300 font-medium' 
                  : selectedPartner === 'global'
                  ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                  : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
              }`}
            >
              {selectedPartner !== 'global' ? selectedPartner?.toUpperCase() : 'Instancia'}
            </button>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <label className="text-sm text-slate-600 font-medium">Período:</label>
            <select
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              className="px-3 py-1.5 border rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500"
              data-testid="period-select"
            >
              <option value="mtd">Mes Actual</option>
              <option value="ytd">Año 2026</option>
              <option value="last30">Últimos 30 días</option>
              <option value="last90">Últimos 90 días</option>
            </select>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleExportCSV}
            disabled={exporting || incidencias.length === 0}
            className="flex items-center gap-1.5"
            data-testid="export-csv-btn"
            title={incidencias.length === 0 ? 'No hay datos para exportar con los filtros actuales' : undefined}
          >
            <Download className="w-4 h-4" />
            {exporting ? 'Exportando...' : 'Descargar Excel'}
          </Button>
          <button
            onClick={() => fetchData(true)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            title="Actualizar datos"
          >
            <RefreshCw className="w-4 h-4 text-gray-500" />
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-white border-blue-100">
          <CardContent className="pt-4">
            <div className="flex items-center gap-1">
              <p className="text-sm text-blue-600 font-medium">Incidencias FCR=Sí</p>
              <InfoTooltip text="Incidencias del máster que DEBERÍAN resolverse en 1er contacto" />
            </div>
            <p className="text-3xl font-bold text-blue-700">
              {resumen.incidencias_con_tickets || 0}
              <span className="text-lg text-blue-400 font-normal">/{resumen.total_incidencias_fcr_si || 0}</span>
            </p>
            <p className="text-xs text-blue-500 mt-1">con tickets en el período</p>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-emerald-50 to-white border-emerald-100">
          <CardContent className="pt-4">
            <div className="flex items-center gap-1">
              <p className="text-sm text-emerald-600 font-medium">FCR Global</p>
              <InfoTooltip text="% de tickets resueltos en 1er contacto (sin escalado ni reapertura)" />
            </div>
            <p className="text-3xl font-bold text-emerald-700">{resumen.fcr_global_pct || 0}%</p>
            <p className="text-xs text-emerald-500 mt-1">{resumen.total_tickets_analizados?.toLocaleString() || 0} tickets</p>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-orange-50 to-white border-orange-100">
          <CardContent className="pt-4">
            <div className="flex items-center gap-1">
              <p className="text-sm text-orange-600 font-medium">Problema PROCESO</p>
              <InfoTooltip text="Incidencias donde la brecha es transversal (todos los agentes fallan) → revisar herramienta/máster" />
            </div>
            <p className="text-3xl font-bold text-orange-700">{resumen.incidencias_problema_proceso || 0}</p>
            <p className="text-xs text-orange-500 mt-1">incidencias a revisar</p>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-indigo-50 to-white border-indigo-100">
          <CardContent className="pt-4">
            <div className="flex items-center gap-1">
              <p className="text-sm text-indigo-600 font-medium">Problema PERSONA</p>
              <InfoTooltip text="Incidencias donde la brecha está concentrada en pocos agentes → capacitación" />
            </div>
            <p className="text-3xl font-bold text-indigo-700">{resumen.incidencias_problema_persona || 0}</p>
            <p className="text-xs text-indigo-500 mt-1">para capacitación</p>
          </CardContent>
        </Card>
      </div>

      {/* Tabla de incidencias */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Incidencias FCR (ordenadas por brecha)
              <InfoTooltip text="Las primeras son las que más casos de brecha tienen → foco" />
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          {incidencias.length === 0 ? (
            <EmptyState 
              title="No hay datos de FCR por incidencia"
              message="No se encontraron tickets de incidencias con fcr=sí en el período."
              icon={Target}
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="fcr-incidencia-table">
                <thead>
                  <tr className="border-b bg-slate-50">
                    <th className="text-left py-3 px-3 font-medium text-slate-600">Incidencia</th>
                    <th className="text-right py-3 px-2 font-medium text-slate-600">
                      Total
                      <InfoTooltip text="Total de tickets de esta incidencia en el período" />
                    </th>
                    <th className="text-right py-3 px-2 font-medium text-slate-600">
                      FCR Real
                      <InfoTooltip text="% resueltos en 1er contacto (sin escalado ni reapertura)" />
                    </th>
                    <th className="text-right py-3 px-2 font-medium text-slate-600">
                      Brecha
                      <InfoTooltip text="Casos que NO se resolvieron en 1er contacto" />
                    </th>
                    <th className="text-center py-3 px-2 font-medium text-slate-600">
                      Tipo
                      <InfoTooltip text="PROCESO = transversal (herramienta) | PERSONA = concentrada (capacitación)" />
                    </th>
                    <th className="text-center py-3 px-2 font-medium text-slate-600">
                      Tendencia
                      <InfoTooltip text="↑ mejora, ↓ empeora, → estable (comparando inicio vs fin del período)" />
                    </th>
                    <th className="text-center py-3 px-2 font-medium text-slate-600">Agentes</th>
                    <th className="text-center py-3 px-2 font-medium text-slate-600"></th>
                  </tr>
                </thead>
                <tbody>
                  {incidencias.map((inc, idx) => {
                    const tipoStyle = getTipoBrechaStyle(inc.tipo_brecha);
                    const TipoIcon = tipoStyle.icon;
                    const isExpanded = expandedRows.has(inc.incidencia);
                    
                    return (
                      <React.Fragment key={inc.incidencia}>
                        <tr className={`border-b hover:bg-slate-50 transition-colors ${idx < 5 ? 'bg-red-50/30' : ''}`}>
                          <td className="py-3 px-3">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-slate-800">{inc.incidencia}</span>
                              {idx < 3 && (
                                <Badge variant="destructive" className="text-xs">TOP {idx + 1}</Badge>
                              )}
                            </div>
                            <span className="text-xs text-slate-500">{inc.area_destino}</span>
                          </td>
                          <td className="text-right py-3 px-2 font-medium text-slate-700">
                            {inc.total_tickets}
                          </td>
                          <td className="text-right py-3 px-2">
                            <span className={`font-semibold ${inc.fcr_real_pct >= 70 ? 'text-green-600' : inc.fcr_real_pct >= 50 ? 'text-amber-600' : 'text-red-600'}`}>
                              {inc.fcr_real_pct}%
                            </span>
                          </td>
                          <td className="text-right py-3 px-2">
                            <div className="flex flex-col items-end">
                              <span className="font-bold text-red-600">{inc.brecha_casos}</span>
                              <span className="text-xs text-slate-500">{inc.brecha_pct}%</span>
                            </div>
                          </td>
                          <td className="text-center py-3 px-2">
                            <Badge className={`${tipoStyle.bg} ${tipoStyle.text} ${tipoStyle.border} border`}>
                              <TipoIcon className="w-3 h-3 mr-1" />
                              {inc.tipo_brecha}
                            </Badge>
                          </td>
                          <td className="text-center py-3 px-2">
                            {getTendenciaIcon(inc.tendencia)}
                          </td>
                          <td className="text-center py-3 px-2 text-slate-600">
                            {inc.num_agentes}
                            {inc.rango_fcr_agentes != null && (
                              <span className="text-xs text-slate-400 block">
                                ({inc.fcr_min_agente}-{inc.fcr_max_agente}%)
                              </span>
                            )}
                          </td>
                          <td className="text-center py-3 px-2">
                            {inc.top_agentes_brecha?.length > 0 && (
                              <button
                                onClick={() => toggleRow(inc.incidencia)}
                                className="p-1 hover:bg-slate-200 rounded transition-colors"
                              >
                                {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                              </button>
                            )}
                          </td>
                        </tr>
                        
                        {/* Drill-down: Top agentes con brecha */}
                        {isExpanded && inc.top_agentes_brecha?.length > 0 && (
                          <tr className="bg-slate-50/70">
                            <td colSpan={8} className="py-2 px-6">
                              <div className="text-xs text-slate-600">
                                <span className="font-medium">Top agentes con mayor brecha:</span>
                                <div className="flex flex-wrap gap-3 mt-1">
                                  {inc.top_agentes_brecha.map((a, i) => (
                                    <span key={i} className="bg-white px-2 py-1 rounded border">
                                      {a.agente}: <span className={a.fcr_pct < 50 ? 'text-red-600 font-medium' : 'text-slate-700'}>{a.fcr_pct}%</span>
                                      <span className="text-slate-400 ml-1">({a.total} tickets)</span>
                                    </span>
                                  ))}
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Panel de definiciones */}
      <DefinitionsPanel 
        definitions={{
          'FCR Real': 'Resuelto en 1er contacto: Sin pasar por Escalado ni Reabierto',
          'Brecha': 'Tickets que NO se resolvieron en 1er contacto (escalados o reabiertos)',
          'PROCESO': 'Brecha transversal - La mayoría de agentes fallan → revisar proceso/herramienta/máster',
          'PERSONA': 'Brecha concentrada - Solo algunos agentes fallan → capacitación'
        }}
      />
    </div>
  );
}
